import api from './api';
import Cookies from 'js-cookie';

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'user' | 'admin' | 'super-admin';
  avatar?: string;
  phone?: string;
  company?: string;
  department?: string;
  bio?: string;
  lastLoginAt?: string;
  emailVerified?: boolean; // Made optional since it's not in the backend entity
  // Temporary compatibility fields - will be removed after migration
  externalUserId?: number;
  onlyDashboard?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ExternalUser {
  id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  avatar?: string;
  phone?: string;
  company?: string;
  linkedin?: string;
  primaryRole?: string;
  hasPassword?: boolean;
  lastAccess?: string;
  department?: string;
  externalUserId: number;
  externalStatus:
    | 'NOT_APPLICABLE'
    | 'PENDING_CREATE'
    | 'CREATED'
    | 'PENDING_SYNC'
    | 'SYNCED'
    | 'ERROR';
  lastSyncAt?: string;
  createdAt: string;
  updatedAt: string;
  sessionCount?: number; // Added for chat session count display
}

// Union type for components that need to handle both types
export type AnyUser = User | ExternalUser;

// Type guard to check if a user is a External user
export function isExternalUser(user: AnyUser): user is ExternalUser {
  return 'externalUserId' in user;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  user: User;
}

export interface RegisterResponse {
  message: string;
  requiresApproval: boolean;
}

export interface UpdateProfileDto {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  company?: string;
  department?: string;
  bio?: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
  confirmPassword: string;
}

export interface ForgotPasswordResponse {
  message: string;
}

export interface ResetPasswordResponse {
  message: string;
}

export interface ValidateTokenResponse {
  valid: boolean;
}

export const authService = {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  async register(data: RegisterRequest): Promise<RegisterResponse> {
    const response = await api.post('/auth/register', data);
    return response.data;
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.get('/auth/me');
    return response.data;
  },

  async updateProfile(data: UpdateProfileDto): Promise<User> {
    const response = await api.put('/auth/profile', data);
    return response.data;
  },

  async forgotPassword(
    data: ForgotPasswordRequest
  ): Promise<ForgotPasswordResponse> {
    const response = await api.post('/auth/forgot-password', data);
    return response.data;
  },

  async resetPassword(
    data: ResetPasswordRequest
  ): Promise<ResetPasswordResponse> {
    const response = await api.post('/auth/reset-password', data);
    return response.data;
  },

  async validateResetToken(token: string): Promise<ValidateTokenResponse> {
    const response = await api.get(`/auth/validate-reset-token?token=${token}`);
    return response.data;
  },

  logout() {
    Cookies.remove('access_token');
    Cookies.remove('user_data');
  },
};
